import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useHybridAuth } from '../contexts/HybridAuthContext';
import CompleteGoogleSignup from '../components/CompleteGoogleSignup';

const GoogleAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken, business, isAuthenticated, loading } = useHybridAuth();
  const [error, setError] = useState<string | null>(null);
  const [needsBusiness, setNeedsBusiness] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (started) return;
      setStarted(true);

      const oauthError = searchParams.get('error');
      if (oauthError) {
        setError(oauthError);
        return;
      }

      const token = searchParams.get('token');
      const isNew = searchParams.get('is_new') === '1';

      if (!token) {
        setError('Missing authentication token from Google sign-in');
        return;
      }

      try {
        await loginWithToken(token);
        if (isNew) {
          setNeedsBusiness(true);
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Google sign-in failed';
        setError(message);
      }
    };

    void run();
  }, [started, searchParams, loginWithToken, navigate]);

  useEffect(() => {
    if (needsBusiness && isAuthenticated && business) {
      navigate('/dashboard', { replace: true });
    }
  }, [needsBusiness, isAuthenticated, business, navigate]);

  if (error) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>Google sign-in failed</h1>
            <p>{error}</p>
          </div>
          <button
            type="button"
            className="login-btn"
            onClick={() => navigate('/', { replace: true })}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  if (needsBusiness && isAuthenticated && !business) {
    return <CompleteGoogleSignup />;
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Signing you in…</h1>
          <p>{loading ? 'Completing Google authentication' : 'Preparing your account'}</p>
        </div>
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
